import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Copy, Link2, Mail, Plus, X } from 'lucide-react';
import { toast } from 'react-toastify';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';
import {
  AdminShadowJob,
  AdminShadowRecruiter,
  AdminShadowRecruiterInvite,
  useAdminStore,
} from '../../stores/adminStore';

const getErrorMessage = (err: unknown): string => {
  // supabase-js resolves RPC failures to the parsed PostgREST error object
  // ({ message, details, hint, code }), not an Error instance, so
  // `error instanceof Error` / `String(error)` (-> "[object Object]") never
  // recovers the message the RPC actually raised.
  const message = (err as { message?: unknown })?.message;
  return typeof message === 'string' ? message : String(err);
};

interface ShadowRecruiterManagerModalProps {
  onClose: () => void;
  selectedJob?: AdminShadowJob | null;
}

interface ShadowRecruiterDraft {
  id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  company_name: string;
  job_ids: string[];
}

const createDraft = (
  recruiter: AdminShadowRecruiter | null,
  selectedJob?: AdminShadowJob | null,
  options: { preselectJob?: boolean } = { preselectJob: true }
): ShadowRecruiterDraft => {
  const base = recruiter?.job_ids ?? [];
  const jobIds =
    options.preselectJob && selectedJob?.id && !base.includes(selectedJob.id)
      ? [...base, selectedJob.id]
      : base;

  return {
    id: recruiter?.id ?? null,
    first_name: recruiter?.first_name ?? '',
    last_name: recruiter?.last_name ?? '',
    email: recruiter?.email ?? '',
    company_name: recruiter?.company_name ?? '',
    job_ids: jobIds,
  };
};

const getRecruiterLabel = (recruiter: AdminShadowRecruiter) => {
  const fullName = `${recruiter.first_name} ${recruiter.last_name}`.trim();
  return fullName || recruiter.email || recruiter.company_name || recruiter.id;
};

const buildJobOptions = (
  jobs: AdminShadowJob[],
  recruiter: AdminShadowRecruiter | null,
  selectedJob?: AdminShadowJob | null
) => {
  const options = new Map<
    string,
    { id: string; title: string; status?: string | null; visibility?: string | null }
  >();

  jobs.forEach(job => {
    options.set(job.id, {
      id: job.id,
      title: job.title,
      status: job.status,
      visibility: job.visibility,
    });
  });

  recruiter?.jobs.forEach(job => {
    if (!options.has(job.id)) {
      options.set(job.id, {
        id: job.id,
        title: job.title,
        status: job.status ?? null,
        visibility: job.visibility ?? null,
      });
    }
  });

  if (selectedJob && !options.has(selectedJob.id)) {
    options.set(selectedJob.id, {
      id: selectedJob.id,
      title: selectedJob.title,
      status: selectedJob.status,
      visibility: selectedJob.visibility,
    });
  }

  return Array.from(options.values()).sort((left, right) => left.title.localeCompare(right.title));
};

const ShadowRecruiterManagerModal: React.FC<ShadowRecruiterManagerModalProps> = ({
  onClose,
  selectedJob,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const {
    jobs,
    shadowRecruiters,
    shadowRecruiterInvite,
    fetchShadowRecruiters,
    upsertShadowRecruiter,
    issueShadowRecruiterInvite,
  } = useAdminStore();
  const [selectedRecruiterId, setSelectedRecruiterId] = useState<string | null>(
    selectedJob?.shadow_recruiter_id ?? null
  );
  const [draft, setDraft] = useState<ShadowRecruiterDraft>(createDraft(null, selectedJob));
  const [inviteResult, setInviteResult] = useState<AdminShadowRecruiterInvite | null>(null);
  const [saving, setSaving] = useState(false);
  const [issuingInvite, setIssuingInvite] = useState(false);

  const selectedRecruiter =
    shadowRecruiters.find(recruiter => recruiter.id === selectedRecruiterId) ?? null;
  const jobOptions = buildJobOptions(jobs, selectedRecruiter, selectedJob);

  useEffect(() => {
    if (shadowRecruiters.length === 0) {
      void fetchShadowRecruiters();
    }
  }, [fetchShadowRecruiters, shadowRecruiters.length]);

  useEffect(() => {
    if (!selectedJob?.shadow_recruiter_id) return;

    const recruiter =
      shadowRecruiters.find(item => item.id === selectedJob.shadow_recruiter_id) ?? null;

    if (recruiter) {
      setSelectedRecruiterId(current => (current === recruiter.id ? current : recruiter.id));
      setDraft(current =>
        current.id === recruiter.id ? current : createDraft(recruiter, selectedJob)
      );
      setInviteResult(current =>
        current?.shadow_recruiter_id === recruiter.id ? current : (recruiter.latest_invite ?? null)
      );
    }
  }, [selectedJob, shadowRecruiters]);

  useEffect(() => {
    if (
      shadowRecruiterInvite &&
      shadowRecruiterInvite.shadow_recruiter_id === selectedRecruiterId
    ) {
      setInviteResult(shadowRecruiterInvite);
    }
  }, [selectedRecruiterId, shadowRecruiterInvite]);

  const selectRecruiter = (
    recruiter: AdminShadowRecruiter,
    options: { preselectJob?: boolean } = { preselectJob: true }
  ) => {
    setSelectedRecruiterId(recruiter.id);
    setDraft(createDraft(recruiter, selectedJob, options));
    setInviteResult(recruiter.latest_invite ?? null);
  };

  const startCreateFlow = () => {
    setSelectedRecruiterId(null);
    setDraft(createDraft(null, selectedJob));
    setInviteResult(null);
  };

  const toggleJobId = (jobId: string) => {
    setDraft(current => ({
      ...current,
      job_ids: current.job_ids.includes(jobId)
        ? current.job_ids.filter(id => id !== jobId)
        : [...current.job_ids, jobId],
    }));
  };

  const copyText = async (value: string, label: string) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      toast.success(
        t('shadowRecruiters.copied', {
          ns: 'admin',
          defaultValue: '{{label}} copied',
          label,
        })
      );
    } catch (error) {
      console.error('Failed to copy invite value', error);
      toast.error(
        t('shadowRecruiters.copyError', {
          ns: 'admin',
          defaultValue: 'Could not copy this value.',
        })
      );
    }
  };

  const handleSave = async () => {
    if (
      !draft.first_name.trim() ||
      !draft.last_name.trim() ||
      !draft.email.trim() ||
      !draft.company_name.trim()
    ) {
      toast.error(
        t('shadowRecruiters.validation', {
          ns: 'admin',
          defaultValue: 'First name, last name, email, and company name are required.',
        })
      );
      return;
    }

    setSaving(true);
    try {
      const recruiter = await upsertShadowRecruiter({
        id: draft.id,
        first_name: draft.first_name,
        last_name: draft.last_name,
        email: draft.email,
        company_name: draft.company_name,
        job_ids: draft.job_ids,
      });
      const nextRecruiter =
        useAdminStore.getState().shadowRecruiters.find(item => item.id === recruiter.id) ??
        recruiter;

      // preselectJob: false — this refresh must not silently re-add the currently
      // selected job to the draft if it was just unchecked and saved successfully.
      selectRecruiter(nextRecruiter, { preselectJob: false });
      toast.success(
        t('shadowRecruiters.saved', {
          ns: 'admin',
          defaultValue: 'Shadow recruiter saved.',
        })
      );
    } catch (error) {
      console.error('Failed to save shadow recruiter', error);
      const message = getErrorMessage(error);
      const rejectionMarker = 'SHADOW_RECRUITER_JOB_ASSIGNMENT_REJECTED';
      if (message.includes(rejectionMarker)) {
        const separatorIndex = message.indexOf(rejectionMarker) + rejectionMarker.length;
        const rejectedTitles = message
          .slice(separatorIndex)
          .replace(/^[:\s]+/, '')
          .trim();
        toast.error(
          t('shadowRecruiters.assignRejected', {
            ns: 'admin',
            defaultValue:
              'Nothing was saved: {{jobs}} already belong to another recruiter and cannot be assigned to this shadow recruiter.',
            jobs: rejectedTitles,
          })
        );
      } else if (message.includes('SHADOW_RECRUITER_ALREADY_CLAIMED')) {
        toast.error(
          t('shadowRecruiters.alreadyClaimed', {
            ns: 'admin',
            defaultValue:
              'This shadow recruiter has already claimed their account. Refresh the page to see the latest details.',
          })
        );
      } else {
        toast.error(
          t('shadowRecruiters.saveError', {
            ns: 'admin',
            defaultValue: 'Could not save the shadow recruiter.',
          })
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleIssueInvite = async () => {
    if (!selectedRecruiterId) return;

    setIssuingInvite(true);
    try {
      const invite = await issueShadowRecruiterInvite(selectedRecruiterId);
      setInviteResult(invite);
      toast.success(
        t('shadowRecruiters.inviteIssued', {
          ns: 'admin',
          defaultValue: 'Invite issued.',
        })
      );
    } catch (error) {
      console.error('Failed to issue shadow recruiter invite', error);
      toast.error(
        t('shadowRecruiters.inviteError', {
          ns: 'admin',
          defaultValue: 'Could not issue the invite.',
        })
      );
    } finally {
      setIssuingInvite(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[72rem]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-secondary">
            {t('shadowRecruiters.title', {
              ns: 'admin',
              defaultValue: 'Shadow recruiters',
            })}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('shadowRecruiters.description', {
              ns: 'admin',
              defaultValue:
                'Create or edit shadow recruiters, issue invite links, and associate them with the jobs currently loaded in admin.',
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          aria-label={t('common:form_actions.close', { ns: 'common', defaultValue: 'Close' })}
        >
          <X size={18} />
        </button>
      </div>

      {selectedJob ? (
        <div className="mt-4 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-gray-700">
          {t('shadowRecruiters.selectedJobHint', {
            ns: 'admin',
            defaultValue: 'Managing shadow recruiter association for "{{title}}"',
            title: selectedJob.title,
          })}
        </div>
      ) : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-[18rem,minmax(0,1fr)]">
        <aside className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              {t('shadowRecruiters.listTitle', {
                ns: 'admin',
                defaultValue: 'Existing recruiters',
              })}
            </h3>
            <button
              type="button"
              onClick={startCreateFlow}
              className="inline-flex items-center gap-1 rounded-full border border-primary/20 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/5"
            >
              <Plus size={14} />
              {t('shadowRecruiters.new', {
                ns: 'admin',
                defaultValue: 'New',
              })}
            </button>
          </div>

          <div className="max-h-[28rem] space-y-2 overflow-y-auto pr-1">
            {shadowRecruiters.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-6 text-sm text-gray-500">
                {t('shadowRecruiters.empty', {
                  ns: 'admin',
                  defaultValue: 'No shadow recruiters yet.',
                })}
              </div>
            ) : (
              shadowRecruiters.map(recruiter => {
                const isSelected = recruiter.id === selectedRecruiterId;
                return (
                  <button
                    key={recruiter.id}
                    type="button"
                    onClick={() => selectRecruiter(recruiter)}
                    className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-primary/40 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">
                      {getRecruiterLabel(recruiter)}
                    </div>
                    {recruiter.company_name ? (
                      <div className="mt-1 text-sm text-gray-600">{recruiter.company_name}</div>
                    ) : null}
                    {recruiter.email ? (
                      <div className="mt-1 text-xs text-gray-500">{recruiter.email}</div>
                    ) : null}
                    <div className="mt-2 text-xs text-gray-500">
                      {t('shadowRecruiters.jobCount', {
                        ns: 'admin',
                        defaultValue: '{{count}} associated jobs',
                        count: recruiter.job_ids.length,
                      })}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <section className="space-y-6">
          <div className="rounded-3xl border border-gray-200 p-5">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedRecruiterId
                    ? t('shadowRecruiters.editTitle', {
                        ns: 'admin',
                        defaultValue: 'Edit shadow recruiter',
                      })
                    : t('shadowRecruiters.createTitle', {
                        ns: 'admin',
                        defaultValue: 'Create shadow recruiter',
                      })}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('shadowRecruiters.formHint', {
                    ns: 'admin',
                    defaultValue:
                      'Attach jobs here when you want the admin list and job details to reflect the shadow recruiter relationship.',
                  })}
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="text-sm font-semibold text-gray-700">
                {t('first_name', { ns: 'common', defaultValue: 'First name' })}
                <input
                  value={draft.first_name}
                  onChange={event =>
                    setDraft(current => ({ ...current, first_name: event.target.value }))
                  }
                  className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-2 font-normal text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </label>

              <label className="text-sm font-semibold text-gray-700">
                {t('last_name', { ns: 'common', defaultValue: 'Last name' })}
                <input
                  value={draft.last_name}
                  onChange={event =>
                    setDraft(current => ({ ...current, last_name: event.target.value }))
                  }
                  className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-2 font-normal text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </label>

              <label className="text-sm font-semibold text-gray-700">
                {t('email', { ns: 'common', defaultValue: 'Email' })}
                <input
                  type="email"
                  value={draft.email}
                  onChange={event =>
                    setDraft(current => ({ ...current, email: event.target.value }))
                  }
                  className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-2 font-normal text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </label>

              <label className="text-sm font-semibold text-gray-700">
                {t('table_fields.company_name', {
                  ns: 'admin',
                  defaultValue: 'Company name',
                })}
                <input
                  value={draft.company_name}
                  onChange={event =>
                    setDraft(current => ({ ...current, company_name: event.target.value }))
                  }
                  className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-2 font-normal text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </label>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    {t('shadowRecruiters.jobsTitle', {
                      ns: 'admin',
                      defaultValue: 'Associated jobs',
                    })}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {t('shadowRecruiters.jobsHint', {
                      ns: 'admin',
                      defaultValue:
                        'These options come from the jobs currently loaded in this admin view.',
                    })}
                  </p>
                </div>
              </div>

              <div className="mt-3 max-h-[18rem] space-y-2 overflow-y-auto rounded-2xl border border-gray-200 p-3">
                {jobOptions.length === 0 ? (
                  <div className="text-sm text-gray-500">
                    {t('shadowRecruiters.noJobs', {
                      ns: 'admin',
                      defaultValue: 'No loaded jobs are available to associate right now.',
                    })}
                  </div>
                ) : (
                  jobOptions.map(job => (
                    <label
                      key={job.id}
                      className="flex cursor-pointer items-start gap-3 rounded-2xl border border-transparent px-2 py-2 hover:border-gray-200 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={draft.job_ids.includes(job.id)}
                        onChange={() => toggleJobId(job.id)}
                        className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <div>
                        <div className="font-medium text-gray-900">{job.title}</div>
                        <div className="text-xs text-gray-500">
                          {[job.status, job.visibility].filter(Boolean).join(' • ') || job.id}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <Button variant="simple-text" type="button" onClick={onClose} disabled={saving}>
                {t('common:form_actions.cancel', { ns: 'common', defaultValue: 'Cancel' })}
              </Button>
              <Button variant="primary" type="button" onClick={handleSave} disabled={saving}>
                {saving
                  ? t('loading', { ns: 'common', defaultValue: 'Loading...' })
                  : t('common:form_actions.save', { ns: 'common', defaultValue: 'Save' })}
              </Button>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-200 p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('shadowRecruiters.inviteTitle', {
                    ns: 'admin',
                    defaultValue: 'Invite link',
                  })}
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedRecruiterId
                    ? t('shadowRecruiters.inviteHintReady', {
                        ns: 'admin',
                        defaultValue:
                          'Issue a fresh invite token when the shadow recruiter is ready to claim their account.',
                      })
                    : t('shadowRecruiters.inviteHintSaveFirst', {
                        ns: 'admin',
                        defaultValue: 'Save the recruiter first before issuing an invite.',
                      })}
                </p>
              </div>
              <Button
                variant="alternate"
                type="button"
                onClick={handleIssueInvite}
                disabled={!selectedRecruiterId || issuingInvite}
              >
                {issuingInvite
                  ? t('loading', { ns: 'common', defaultValue: 'Loading...' })
                  : t('shadowRecruiters.issueInvite', {
                      ns: 'admin',
                      defaultValue: 'Issue invite',
                    })}
              </Button>
            </div>

            {inviteResult ? (
              <div className="mt-5 space-y-4 rounded-2xl border border-primary/15 bg-primary/5 p-4">
                <div className="flex items-start gap-3 text-sm text-gray-700">
                  <Mail size={16} className="mt-0.5 text-primary" />
                  <div>
                    <div className="font-semibold text-gray-900">
                      {selectedRecruiter ? getRecruiterLabel(selectedRecruiter) : draft.email}
                    </div>
                    <div>{selectedRecruiter?.email || draft.email}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t('shadowRecruiters.inviteLinkLabel', {
                      ns: 'admin',
                      defaultValue: 'Invite link',
                    })}
                  </div>
                  <div className="flex flex-col gap-2 rounded-2xl bg-white px-3 py-3 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0 text-sm text-gray-700">{inviteResult.invite_url}</div>
                    <button
                      type="button"
                      onClick={() =>
                        copyText(
                          inviteResult.invite_url,
                          t('shadowRecruiters.inviteLinkLabel', {
                            ns: 'admin',
                            defaultValue: 'Invite link',
                          })
                        )
                      }
                      className="inline-flex items-center gap-2 text-sm font-semibold text-primary"
                    >
                      <Link2 size={16} />
                      {t('copy_link', { ns: 'common', defaultValue: 'Copy link' })}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t('shadowRecruiters.tokenLabel', {
                      ns: 'admin',
                      defaultValue: 'Invite token',
                    })}
                  </div>
                  <div className="flex flex-col gap-2 rounded-2xl bg-white px-3 py-3 md:flex-row md:items-center md:justify-between">
                    <div className="text-sm text-gray-700">{inviteResult.token}</div>
                    <button
                      type="button"
                      onClick={() =>
                        copyText(
                          inviteResult.token,
                          t('shadowRecruiters.tokenLabel', {
                            ns: 'admin',
                            defaultValue: 'Invite token',
                          })
                        )
                      }
                      className="inline-flex items-center gap-2 text-sm font-semibold text-primary"
                    >
                      <Copy size={16} />
                      {t('copy', { ns: 'common', defaultValue: 'Copy' })}
                    </button>
                  </div>
                </div>

                {inviteResult.expires_at ? (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar size={14} />
                    {t('shadowRecruiters.expiresAt', {
                      ns: 'admin',
                      defaultValue: 'Expires at {{value}}',
                      value: new Date(inviteResult.expires_at).toLocaleString(),
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </FullBlockingModal>
  );
};

export default ShadowRecruiterManagerModal;
